import React from 'react';
import MediaThumbnail from './MediaThumbnail';
import PropTypes from 'prop-types';
import {
  ButtonWithIcon,
  CardSkeletonLoader,
  LanguageTabs
} from '@/components/atoms';
import {Accordion, AccordionItem} from '@/components/molecules';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import MediaPreviewModal from './MediaPreviewModal';
import {getFileNameFromUrl} from '@/helpers/shadcn/utils';
import {formatActivityTypeForReview} from '@/helpers/activityUtils';
import {useVideoActivityReview} from './useVideoActivityReview';
import ActivitySegmentsReviewSection from '@/containers/Activity/ActivityReview/components/ActivitySegmentsReviewSection';

const VIDEO_SPACING = {
  accordionGapClass: 'space-y-5',
  threeColGridClass: 'grid-cols-3 gap-6'
};

const formatDateYYYYMMDD = (value, getFormattedDate) =>
  getFormattedDate(value, false, '-', 'yyyy-mm-dd');

const VideoReviewGridRow = ({items, dataTestId, preserveRhythm = false}) => (
  <div
    className={`mt-3 grid ${VIDEO_SPACING.threeColGridClass}`}
    data-testid={dataTestId}>
    {items.map((item) =>
      item?.isSpacer ? (
        <div
          key={item.label || 'grid-spacer'}
          className='invisible pb-2'
          aria-hidden='true'
        />
      ) : (
        <div key={item.label} className='pb-2'>
          <p className='pl-1 text-sm text-[#D0D0D0]'>{item.label}</p>
          <p className='pl-1 font-bold text-white'>{item.value}</p>
        </div>
      )
    )}
    {preserveRhythm &&
      items.length < 3 &&
      Array.from({length: 3 - items.length}).map((_, idx) => {
        const paddingKey = `padding-${items.length}-${idx === 0 ? 'first' : 'second'}`;
        return (
          <div key={paddingKey} className='invisible pb-2' aria-hidden='true' />
        );
      })}
  </div>
);

VideoReviewGridRow.propTypes = {
  items: PropTypes.array.isRequired,
  dataTestId: PropTypes.string,
  preserveRhythm: PropTypes.bool
};

const VideoReviewSingleRow = ({label, value, dataTestId}) => (
  <VideoReviewGridRow
    dataTestId={dataTestId}
    preserveRhythm
    items={[{label, value}]}
  />
);

VideoReviewSingleRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  dataTestId: PropTypes.string
};

const VideoSectionTitle = ({label, className = ''}) => (
  <div className={className}>
    <h3 className='text-base font-bold text-white'>{label}</h3>
  </div>
);

VideoSectionTitle.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string
};

const VideoActivityReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  isExcluded,
  getFormattedDate
}) => {
  const {
    languages,
    languageCodes,
    imageTabIndex,
    setImageTabIndex,
    videoTabIndex,
    setVideoTabIndex,
    previewModal,
    handlePreview,
    handleClosePreview,
    firstMedia,
    imageActiveLangCode,
    imageActiveTranslation,
    videoActiveLangCode,
    videoActiveTranslation,
    displayOrderValue
  } = useVideoActivityReview({
    activityFormData,
    getFormattedDate,
    isEditable,
    editButtonSchema,
    navigate,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    loading
  });

  return (
    <div
      data-testid='video-activity-review'
      className='video-review-container w-full'>
      {loading && <CardSkeletonLoader count={3} />}
      {!loading && (
        <>
          {showToast && (
            <DuplicateAlert
              type='info'
              message={toastMessage}
              show={true}
              onCloseClick={handleToastClose}
              handleClick={handleViewActivity}
              buttonText='View Activity'
            />
          )}
          <div className='sales-target-review mt-2 w-full'>
            <Accordion
              isDark
              size='large'
              content={
                <div className={`${VIDEO_SPACING.accordionGapClass} w-full`}>
                  {/* Setup Accordion */}
                  <AccordionItem
                    isDark
                    expanded
                    label='Setup'
                    rightAccessoryChild={
                      isEditable ? (
                        <ButtonWithIcon
                          uiSchema={editButtonSchema}
                          iconLeading='edit'
                          handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                        />
                      ) : null
                    }
                    content={
                      <div className='w-full'>
                        <VideoSectionTitle label='Activity' className='mb-3' />
                        <VideoReviewGridRow
                          dataTestId='video-setup-grid-row'
                          preserveRhythm
                          items={[
                            {
                              label: 'Activity Type',
                              value: formatActivityTypeForReview(
                                activityFormData?.activityType
                              )
                            },
                            {
                              label: 'Video Name',
                              value: activityFormData?.activityName || '-'
                            },
                            {
                              label: 'Points Earned',
                              value: activityFormData?.pointsEarned || '-'
                            },
                            {
                              label: 'All Time Limit',
                              value:
                                activityFormData?.allTimeLimit != null &&
                                activityFormData.allTimeLimit !== ''
                                  ? String(activityFormData.allTimeLimit)
                                  : '-'
                            }
                          ]}
                        />

                        <VideoSectionTitle
                          label='Availability'
                          className='mb-3 mt-6'
                        />
                        <VideoReviewGridRow
                          dataTestId='video-availability-grid-row'
                          items={[
                            {
                              label: 'Start Date',
                              value: formatDateYYYYMMDD(
                                activityFormData?.startDate,
                                getFormattedDate
                              )
                            },
                            {
                              label: 'End Date',
                              value: formatDateYYYYMMDD(
                                activityFormData?.endDate,
                                getFormattedDate
                              )
                            },
                            {isSpacer: true, label: 'availability-spacer'}
                          ]}
                        />

                        <ActivitySegmentsReviewSection
                          activityFormData={activityFormData}
                          isExcluded={isExcluded}
                          RowComponent={VideoReviewSingleRow}
                          SectionTitleComponent={VideoSectionTitle}
                          testIdPrefix='video'
                          showAllCustomersRow
                          showExcludedRow
                        />
                      </div>
                    }
                  />

                  {/* Display Accordion */}
                  <AccordionItem
                    isDark
                    expanded
                    label='Display'
                    rightAccessoryChild={
                      isEditable ? (
                        <ButtonWithIcon
                          uiSchema={editButtonSchema}
                          iconLeading='edit'
                          handleButton={() => navigate(ROUTES.ACTIVITY_DISPLAY)}
                        />
                      ) : null
                    }
                    content={
                      <div className='w-full'>
                        <VideoSectionTitle
                          label='Video Display'
                          className='mb-4'
                        />

                        {/* ===== Video Image Section ===== */}
                        <div className='mb-6'>
                          <p className='mb-3 text-sm font-semibold text-[#D0D0D0]'>
                            Video Image
                          </p>

                          {/* Thumbnail + Filename row */}
                          <div
                            className='flex items-start gap-4'
                            data-testid='video-image-thumbnail-row'>
                            <MediaThumbnail
                              url={firstMedia?.videoImageUrl}
                              alt='video-image'
                              type='image'
                              onPreview={() =>
                                handlePreview(
                                  firstMedia?.videoImageUrl,
                                  'image',
                                  'Video Image Preview'
                                )
                              }
                            />
                            <div className='pb-2'>
                              <p className='text-sm text-[#D0D0D0]'>
                                Video Image
                              </p>
                              <p className='font-bold text-white'>
                                {firstMedia?.videoImageName ||
                                  getFileNameFromUrl(firstMedia?.videoImageUrl)}
                              </p>
                            </div>
                          </div>

                          {/* Language tabs for Name + Tag Description */}
                          {languageCodes.length > 0 && (
                            <div className='mt-4'>
                              <LanguageTabs
                                languages={languageCodes}
                                fullLanguages={languages}
                                activeTabIndex={imageTabIndex}
                                setActiveTabIndex={setImageTabIndex}
                                testIdPrefix='review-image-tab'
                              />
                            </div>
                          )}

                          {/* Name + Tag Description below tabs */}
                          <div
                            className='mt-4 flex flex-row items-start gap-4'
                            data-testid={`video-image-fields-${imageActiveLangCode}`}>
                            <div className='w-[386px] flex-none flex-grow-0'>
                              <p className='text-sm text-[#D0D0D0]'>Name</p>
                              <p className='font-bold text-white'>
                                {imageActiveTranslation.name || '-'}
                              </p>
                            </div>
                            <div className='w-[386px] flex-none flex-grow-0'>
                              <p className='text-sm text-[#D0D0D0]'>
                                Tag Description
                              </p>
                              <p className='font-bold text-white'>
                                {imageActiveTranslation.tagDescription || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className='mt-6 border-b border-[color:var(--color-border-divider)]' />

                        {/* ===== Video Section ===== */}
                        <div className='mb-6 mt-6'>
                          <p className='mb-3 text-sm font-semibold text-[#D0D0D0]'>
                            Video
                          </p>

                          {/* Thumbnail + Filename row */}
                          <div
                            className='flex items-start gap-4'
                            data-testid='video-thumbnail-row'>
                            <MediaThumbnail
                              url={firstMedia?.videoThumbnailUrl}
                              alt='video'
                              type='video'
                              downloadUrl={firstMedia?.videoUrl}
                              onPreview={() =>
                                handlePreview(
                                  firstMedia?.videoThumbnailUrl,
                                  'image',
                                  'Video Preview'
                                )
                              }
                            />
                            <div className='pb-2'>
                              <p className='text-sm text-[#D0D0D0]'>Video</p>
                              <p className='font-bold text-white'>
                                {getFileNameFromUrl(firstMedia?.videoUrl)}
                              </p>
                            </div>
                          </div>

                          {/* Language tabs for Description */}
                          {languageCodes.length > 0 && (
                            <div className='mt-4'>
                              <LanguageTabs
                                languages={languageCodes}
                                fullLanguages={languages}
                                activeTabIndex={videoTabIndex}
                                setActiveTabIndex={setVideoTabIndex}
                                testIdPrefix='review-video-tab'
                              />
                            </div>
                          )}

                          {/* Description below tabs */}
                          <div
                            className='mt-4'
                            data-testid={`video-desc-fields-${videoActiveLangCode}`}>
                            <div className='pb-2'>
                              <p className='text-sm text-[#D0D0D0]'>
                                Description
                              </p>
                              <div
                                className='review_value rich-text-content font-bold text-white'
                                data-testid='video-review-html-value'
                                dangerouslySetInnerHTML={{
                                  __html:
                                    videoActiveTranslation.description || ''
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Display Order */}
                        <div className='mt-4'>
                          <div className='pb-2'>
                            <p className='text-sm text-[#D0D0D0]'>
                              Display Order
                            </p>
                            <p className='font-bold text-white'>
                              {displayOrderValue}
                            </p>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </div>
              }
            />
          </div>

          {/* Media Preview Modal */}
          <MediaPreviewModal
            showModal={previewModal.show}
            onClose={handleClosePreview}
            mediaUrl={previewModal.url}
            mediaType={previewModal.type}
            title={previewModal.title}
          />
        </>
      )}
    </div>
  );
};

VideoActivityReview.propTypes = {
  loading: PropTypes.bool.isRequired,
  showToast: PropTypes.bool.isRequired,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func.isRequired,
  handleViewActivity: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  editButtonSchema: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object.isRequired,
  isExcluded: PropTypes.bool.isRequired,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(VideoActivityReview);
